// src/components/features/Navigacia/tour/sections/ciselneZapisy.js
import { i18n } from "@/i18n";
import { steps as videoSteps, branch as videoBranch } from "./naucneVidea";
import {
  steps as classroomSteps,
  branch as classroomBranch,
} from "./mojaUcebna";
import avatarLike from "@/assets/images/gallery/avatar-like.png";

const translate = (...args) => i18n.global.t(...args);

export function steps() {
  const listingSections = [
    ".computer.zapisy",
    ".mobile .zapisy",
    ".zapisy.tablet",
  ];

  const listingRoots = [
    ".computer.zapisy .scroll",
    ".mobile .zapisy",
    ".zapisy.tablet .scroll",
  ];

  const firstCardRoots = listingRoots.map(
    (root) => `${root} .box-zapis-flex:first-of-type .zapis`
  );

  const firstCardSelectors = (suffixes) =>
    firstCardRoots.flatMap((root) =>
      suffixes.map((suffix) => `${root}${suffix}`)
    );

  const firstCardTitleSelectors = firstCardSelectors([
    " .nadpis p",
    " .img-box-zapis .cena p",
    " .img-box-zapis .cena h4",
    " .name p",
  ]);

  const firstCardCardSelectors = firstCardSelectors([" .img-box-zapis", ""]);

  const firstCardCartSelectors = firstCardSelectors([
    " .button .rozdeleny-button:not(.hrat-ukazku)",
    " .akcia .rozdeleny-button:not(.hrat-ukazku)",
  ]);

  const firstCardPreviewSelectors = firstCardSelectors([
    " .button .hrat-ukazku",
    " .akcia .hrat-ukazku",
  ]);

  const t = translate;

  const waitForElement = (selector, timeout = 6000, interval = 160) => {
    if (typeof window === "undefined") return Promise.resolve(null);
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() - start > timeout) {
          resolve(null);
          return;
        }
        setTimeout(tick, interval);
      };
      tick();
    });
  };

  const scrollElementIntoView = (el) => {
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (_) {
      el.scrollIntoView();
    }
  };

  const isDetailPage = () => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    return (
      window.location.pathname.includes("/ucebna/ciselny-zapis") &&
      params.has("cislo")
    );
  };

  const ensureDetailPage = () => {
    if (typeof window === "undefined") return Promise.resolve();
    return new Promise((resolve) => {
      const ready = () => {
        const player = document.querySelector(
          "[data-tour='sheet-audio-player']"
        );
        if (player && isDetailPage()) {
          resolve();
          return true;
        }
        return false;
      };

      if (ready()) return;

      const trigger = document.querySelector("[data-tour='owned-songs-play']");
      if (trigger && typeof trigger.click === "function") trigger.click();

      const start = Date.now();
      const interval = 180;
      const check = () => {
        if (ready()) return;
        if (Date.now() - start > 6000) {
          resolve();
          return;
        }
        setTimeout(check, interval);
      };
      check();
    });
  };

  return [
    // 1) ZOZNAM – veľký obsah so zápismi
    {
      goto: "/ciselne-zapisy",
      selectors: listingSections,
      title: t("tour.sections.ciselneZapisy.steps.overview.title"),
      text: t("tour.sections.ciselneZapisy.steps.overview.text"),
      side: "top",
      pad: { x: 24, y: 16 },
      radius: 18,
    },

    // 2) VYHĽADÁVAČ – len samotné INPUT pole
    {
      selectors: [
        ".computer.vyber .categories-search .search input",
        ".mobile .vyber .categories-search .search input",
      ],
      title: t("tour.sections.ciselneZapisy.steps.search.title"),
      text: t("tour.sections.ciselneZapisy.steps.search.text"),
      side: "bottom",
      pad: { x: 18, y: 10 },
      radius: 12,
    },

    // === LAYOUT: zobrazenie do kariet (grid)
    {
      selectors: [
        ".computer.vyber .layouts > img:first-of-type",
        ".mobile .vyber .layouts > img:first-of-type",
      ],
      title: t("tour.sections.ciselneZapisy.steps.gridView.title"),
      text: t("tour.sections.ciselneZapisy.steps.gridView.text"),
      side: "top",
      pad: { x: 16, y: 10 },
      radius: 12,
    },

    // === LAYOUT: zobrazenie do zoznamu (rows)
    {
      selectors: [
        ".computer.vyber .layouts > img:nth-of-type(2)",
        ".mobile .vyber .layouts > img:nth-of-type(2)",
      ],
      title: t("tour.sections.ciselneZapisy.steps.listView.title"),
      text: t("tour.sections.ciselneZapisy.steps.listView.text"),
      side: "top",
      pad: { x: 16, y: 10 },
      radius: 12,
    },

    // === DARČEK / KREDITY
    {
      selectors: [
        ".computer.vyber .layouts .credit-toggle",
        ".mobile .vyber .layouts .credit-toggle",
      ],
      title: t("tour.sections.ciselneZapisy.steps.credits.title"),
      text: t("tour.sections.ciselneZapisy.steps.credits.text"),
      side: "top",
      pad: { x: 18, y: 12 },
      radius: 14,
    },

    // 6) KATEGÓRIE – všetky tlačidlá naraz (Polka, Valčík, …)
    {
      selectors: [
        ".computer.vyber .categories .buttons .button",
        ".mobile .vyber .categories .buttons .button",
      ],
      title: t("tour.sections.ciselneZapisy.steps.categories.title"),
      text: t("tour.sections.ciselneZapisy.steps.categories.text"),
      side: "top",
      pad: { x: 22, y: 12 },
      radius: 14,
    },

    // ─────────────────────────────────────────────────────────────
    // DOPLNENÉ – JEDEN ČÍSELNÝ ZÁPIS (prvá položka v zozname)
    // (bez `closest`, aby sa nevybral celý kontajner)
    // ─────────────────────────────────────────────────────────────

    // A) Názov piesne (funguje pre grid / row aj pre zakúpené/nezakúpené)
    {
      selectors: firstCardTitleSelectors,
      title: t("tour.sections.ciselneZapisy.steps.songTitle.title"),
      text: t("tour.sections.ciselneZapisy.steps.songTitle.text"),
      side: "top",
      pad: { x: 16, y: 10 },
      radius: 10,
      async enter() {
        const selector = firstCardTitleSelectors.join(", ");
        const el = await waitForElement(selector, 6000);
        if (el) scrollElementIntoView(el);
      },
    },

    // B) Kartička s detailmi (flip karta – obtiažnosť, stupnice atď.)
    {
      selectors: firstCardCardSelectors,
      title: t("tour.sections.ciselneZapisy.steps.flipCard.title"),
      text: t("tour.sections.ciselneZapisy.steps.flipCard.text"),
      side: "top",
      pad: { x: 16, y: 12 },
      radius: 14,
      async enter() {
        const selector = firstCardCardSelectors.join(", ");
        const el = await waitForElement(selector, 6000);
        if (el) scrollElementIntoView(el);
      },
    },

    // C) Do košíka / Zakúpiť (grid aj row; vylúčime zvukovú ikonu)
    {
      selectors: firstCardCartSelectors,
      title: t("tour.sections.ciselneZapisy.steps.addToCart.title"),
      text: t("tour.sections.ciselneZapisy.steps.addToCart.text"),
      side: "left",
      pad: { x: 16, y: 12 },
      radius: 12,
      async enter() {
        const selector = firstCardCartSelectors.join(", ");
        const el = await waitForElement(selector, 6000);
        if (el) scrollElementIntoView(el);
      },
    },

    // D) Zvuková ukážka (ikonka reproduktora)
    {
      selectors: firstCardPreviewSelectors,
      title: t("tour.sections.ciselneZapisy.steps.preview.title"),
      text: t("tour.sections.ciselneZapisy.steps.preview.text"),
      side: "left",
      pad: { x: 16, y: 12 },
      radius: 12,
      async enter() {
        const selector = firstCardPreviewSelectors.join(", ");
        const el = await waitForElement(selector, 6000);
        if (el) scrollElementIntoView(el);
      },
    },
    // E) Menu – Zoznam piesní
    {
      bind: {
        where: "menu",
        text: t("menu.zoznamPiesni"),
        hrefLike: "moje-piesne",
      },
      title: t("tour.sections.ciselneZapisy.steps.menuList.title"),
      text: t("tour.sections.ciselneZapisy.steps.menuList.text"),
      side: "left",
      pad: { x: 22, y: 12 },
      radius: 14,
      mobileMenu: "open",
      mobileMenuDelay: 260,
      waitFor: 220,
    },

    // F) Podstránka /ucebna/moje-piesne
    {
      goto: "/ucebna/moje-piesne",
      selectors: [
        "section.computer.mobile .scroll h1",
        "section.computer.mobile .scroll h5",
      ],
      title: t("tour.sections.ciselneZapisy.steps.ownedPage.title"),
      text: t("tour.sections.ciselneZapisy.steps.ownedPage.text"),
      side: "top",
      pad: { x: 24, y: 16 },
      radius: 18,
      waitFor: 480,
      mobileMenu: "close",
      mobileMenuDelay: 220,
    },

    // G) Konkrétny vlastnený zápis
    {
      selector: "[data-tour='owned-songs-card']",
      title: t("tour.sections.ciselneZapisy.steps.ownedCard.title"),
      text: t("tour.sections.ciselneZapisy.steps.ownedCard.text"),
      side: "top",
      pad: { x: 20, y: 14 },
      radius: 16,
      waitFor: 520,
    },

    // H) Tlačidlo Hrať na vlastnenom zápise
    {
      selector: "[data-tour='owned-songs-play']",
      title: t("tour.sections.ciselneZapisy.steps.ownedPlay.title"),
      text: t("tour.sections.ciselneZapisy.steps.ownedPlay.text"),
      side: "left",
      pad: { x: 18, y: 12 },
      radius: 12,
      waitFor: 520,
    },

    // I) Detail zápisu – hudobný prehrávač
    {
      selector: "[data-tour='sheet-audio-player']",
      title: t("tour.sections.ciselneZapisy.steps.sheetPlayer.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetPlayer.text"),
      side: "top",
      pad: { x: 24, y: 18 },
      radius: 18,
      waitFor: 820,
      minGuard: 500,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        await ensureDetailPage();
        const player = await waitForElement(
          "[data-tour='sheet-audio-player']",
          4000
        );
        scrollElementIntoView(player);
      },
    },

    // J) Panel s informáciami a textom piesne
    {
      selector: "[data-tour='sheet-info-panel']",
      title: t("tour.sections.ciselneZapisy.steps.sheetPanel.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetPanel.text"),
      side: "right",
      pad: { x: 20, y: 14 },
      radius: 14,
      waitFor: 260,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        await ensureDetailPage();
        const panel = await waitForElement(
          "[data-tour='sheet-info-panel']",
          3000
        );
        scrollElementIntoView(panel);
      },
    },

    // K) Ovládanie priblíženia zápisu
    {
      selector: "[data-tour='sheet-zoom-controls']",
      title: t("tour.sections.ciselneZapisy.steps.sheetZoom.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetZoom.text"),
      side: "top",
      pad: { x: 18, y: 12 },
      radius: 14,
      waitFor: 240,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const controls = await waitForElement(
          "[data-tour='sheet-zoom-controls']",
          3000
        );
        scrollElementIntoView(controls);
      },
    },

    // L) Pridaj medzi obľúbené
    {
      selector: "[data-tour='sheet-favorite-toggle']",
      title: t("tour.sections.ciselneZapisy.steps.sheetFavorite.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetFavorite.text"),
      side: "left",
      pad: { x: 18, y: 14 },
      radius: 14,
      waitFor: 240,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const fav = await waitForElement(
          "[data-tour='sheet-favorite-toggle']",
          3000
        );
        scrollElementIntoView(fav);
      },
    },

    // M) Označ ako zvládnuté
    {
      selector: "[data-tour='sheet-completion-toggle']",
      title: t("tour.sections.ciselneZapisy.steps.sheetCompletion.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetCompletion.text"),
      side: "left",
      pad: { x: 18, y: 14 },
      radius: 14,
      waitFor: 240,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const button = await waitForElement(
          "[data-tour='sheet-completion-toggle']",
          3000
        );
        scrollElementIntoView(button);
      },
    },

    // N) Celá obrazovka pre zápis
    {
      selector: "[data-tour='sheet-fullscreen-toggle']",
      title: t("tour.sections.ciselneZapisy.steps.sheetFullscreen.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetFullscreen.text"),
      side: "left",
      pad: { x: 18, y: 12 },
      radius: 14,
      waitFor: 240,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const button = await waitForElement(
          "[data-tour='sheet-fullscreen-toggle']",
          3000
        );
        scrollElementIntoView(button);
      },
    },

    // O) Hover tlačidlo pre obľúbené piesne
    {
      selector: "[data-tour='sheet-hover-trigger']",
      title: t("tour.sections.ciselneZapisy.steps.sheetHoverPanel.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetHoverPanel.text"),

      side: "top",
      pad: { x: 14, y: 10 },
      radius: 14,
      waitFor: 260,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const list = await waitForElement(
          "[data-tour='sheet-hover-trigger']",
          3000
        );
        scrollElementIntoView(list);
      },
    },

    // P) Playback dock
    {
      selector: "[data-tour='sheet-playback-dock']",
      title: t("tour.sections.ciselneZapisy.steps.sheetPlaybackDock.title"),
      text: t("tour.sections.ciselneZapisy.steps.sheetPlaybackDock.text"),
      side: "top",
      pad: { x: 22, y: 14 },
      radius: 16,
      waitFor: 260,
      lockSheetPaging: true,
      async enter() {
        if (typeof window === "undefined") return;
        const dock = await waitForElement(
          "[data-tour='sheet-playback-dock']",
          3000
        );
        scrollElementIntoView(dock);
      },
    },
  ];
}

export function branch(ctx = {}) {
  const hasCompleted =
    typeof ctx.hasCompleted === "function" ? ctx.hasCompleted : () => false;
  const hasCompletedAny =
    typeof ctx.hasCompletedAny === "function"
      ? ctx.hasCompletedAny
      : (...keys) => {
          const list = [];
          keys.forEach((key) => {
            if (Array.isArray(key)) list.push(...key);
            else if (key != null) list.push(key);
          });
          return list.some((key) => hasCompleted(key));
        };
  const videoDone = hasCompletedAny(["video", "naucne-videa"]);
  const classroomDone = hasCompletedAny(["ucebna", "moja-ucebna"]);

  const options = [];
  const t = translate;
  let text = t("tour.branches.ciselneZapisy.defaultText");
  let planBridgeLabel = t(
    "tour.branches.ciselneZapisy.options.videos.planBridgeLabel"
  );

  if (!videoDone) {
    options.push({
      label: t("tour.branches.ciselneZapisy.options.videos.label"),
      goto: "/naucne-videa",
      key: "video",
      name: "video",
      planLabel: t("tour.branches.ciselneZapisy.options.videos.planLabel"),
      steps: videoSteps,
      branch: videoBranch,
      planBridgeLabel: t(
        "tour.branches.ciselneZapisy.options.videos.planBridgeLabel"
      ),
    });
    text = t("tour.branches.ciselneZapisy.videosText");
  } else if (!classroomDone) {
    options.push({
      label: t("tour.branches.ciselneZapisy.options.classroom.label"),
      goto: "/ucebna",
      key: "ucebna",
      name: "ucebna",
      planLabel: t("tour.branches.ciselneZapisy.options.classroom.planLabel"),
      steps: classroomSteps,
      branch: classroomBranch,
      planBridgeLabel: t(
        "tour.branches.ciselneZapisy.options.classroom.planBridgeLabel"
      ),
    });
    text = t("tour.branches.ciselneZapisy.classroomText");
    planBridgeLabel = t(
      "tour.branches.ciselneZapisy.options.classroom.planBridgeLabel"
    );
  } else {
    options.push({
      label: t("tour.branches.ciselneZapisy.options.finish.label"),
      planLabel: t("tour.branches.ciselneZapisy.options.finish.planLabel"),
      planBridgeLabel: t(
        "tour.branches.ciselneZapisy.options.finish.planBridgeLabel"
      ),
      steps: [],
    });
    text = t("tour.branches.ciselneZapisy.doneText");
    planBridgeLabel = t(
      "tour.branches.ciselneZapisy.options.finish.planBridgeLabel"
    );
  }

  return {
    title: t("tour.branches.ciselneZapisy.title"),
    text,
    avatar: avatarLike,
    planBridgeLabel,
    options,
  };
}
